import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {GRAY_LIGHT} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

import DBEnsemblItemView from './DBEnsemblItemView';
import {
  DBHandle,
  DBImportItemOptions,
  DBImportSequence,
  DBQueryResult,
  SequenceImportOptions,
} from './ImportSequenceConstants';

interface CollapsibleResultContainerProps {
  idx: number;
  isSelected: boolean;
  isExpanded: boolean;
  isSelectionDisabled: boolean;
  onChangeSelected: (checked: boolean) => void;
  onClick: () => void;
  title: string;
  children: JSX.Element;
}

function CollapsibleResultContainer({
  idx,
  isSelected,
  isExpanded,
  isSelectionDisabled,
  onChangeSelected,
  onClick,
  title,
  children,
}: CollapsibleResultContainerProps): JSX.Element {
  return (
    <div>
      <div className={classNames(css(styles.collapsibleHeader), {[css(styles.headerWithBorder)]: idx !== 0})}>
        <CheckboxInput
          id="db_import_result"
          checked={isSelected}
          label=""
          onChange={(e) => {
            onChangeSelected(e.target.checked);
          }}
          disabled={isSelectionDisabled}
        />
        <h4 className={css(styles.headerTitle)} onClick={onClick}>
          {title}
        </h4>
        <div onClick={onClick}>
          <BenchlingIcon icon={isExpanded ? 'chevron-down' : 'chevron-right'} />
        </div>
      </div>
      {isExpanded && children}
    </div>
  );
}

export interface DBImportItemViewProps {
  sequence: DBImportSequence;
  updateImportOptions: (newOptions: Partial<DBImportItemOptions>) => void;
  sequenceImportOptions: SequenceImportOptions;
}

const RESULT_INFO_INPUTS = [
  {label: 'Entry', resultKey: 'name'},
  {label: 'Database', resultKey: 'dbName'},
  {label: 'Type', resultKey: 'type'},
  {label: 'Length', resultKey: 'length'},
  {label: 'Description', resultKey: 'description'},
];

function NonEnsemblItemView({sequence, updateImportOptions}: DBImportItemViewProps): JSX.Element {
  const {resultData, importOptions} = sequence;

  return (
    <div
      className={css(sharedItemStyles.itemFlexContainer, sharedItemStyles.itemContainer)}
      {...getTestProps({component: 'DBImportItem', element: 'item', key: 'result'})}
    >
      <div className={css(styles.itemColumn)}>
        <div className={classNames(css(sharedItemStyles.dbImageColumn), 'media-object')}>
          {resultData.dbImg && (
            <a href={resultData.accLink} target="_blank" rel="noopener noreferrer">
              <img className={css(sharedItemStyles.dbImageColumn)} src={resultData.dbImg} />
            </a>
          )}
        </div>
        <div className={css(sharedItemStyles.fullWidthColumn)}>
          {resultData.sequences != null && (
            <FormGroup>
              <FieldLabel label="Sequence" />
              <SelectDropdown
                displayName={
                  resultData.sequences.length === 0
                    ? 'No sequence found'
                    : resultData.sequences.find((dbSeq) => importOptions.accession === dbSeq.acc)?.name
                }
                disabled={resultData.sequences.length === 0}
                menu={
                  <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL} scrollable={true}>
                    {resultData.sequences.map((seq) => (
                      <Option
                        key={seq.acc}
                        value={seq.acc}
                        isSelected={importOptions.accession === seq.acc}
                        onSelect={(val: string) => {
                          updateImportOptions({accession: val});
                        }}
                      >
                        {seq.name}
                      </Option>
                    ))}
                  </Menu>
                }
              />
            </FormGroup>
          )}
          {RESULT_INFO_INPUTS.map(
            ({label, resultKey}: {label: string; resultKey: keyof DBQueryResult}) =>
              resultData[resultKey] && (
                <div key={label}>
                  <FieldLabel label={label} />
                  <p className={css(sharedItemStyles.itemInfoText)}>{resultData[resultKey]}</p>
                </div>
              )
          )}
        </div>
      </div>
      <div className={css(styles.rightInputsColumn)}>
        <FormGroup>
          <FieldLabel label="Name" />
          <TextInput
            value={importOptions.name}
            onChange={(e) => {
              updateImportOptions({name: e.target.value});
            }}
          />
        </FormGroup>
      </div>
    </div>
  );
}

export interface DBImportItemsProps {
  sequences: Array<DBImportSequence>;
  errorMessage: string | null;
  updateImportOptions: (newOptions: Partial<DBImportItemOptions>, resultIndex: number) => void;
  sequenceImportOptions: SequenceImportOptions;
}

function DBImportItems({
  sequences,
  errorMessage,
  updateImportOptions,
  sequenceImportOptions,
}: DBImportItemsProps): JSX.Element {
  const [expandedSections, setExpandedSections] = useState<Array<number>>(sequences.length === 0 ? [] : [0]);

  function renderResultContent(seq: DBImportSequence, idx: number): JSX.Element {
    const ItemViewComponent = [DBHandle.PRIVATE, DBHandle.ENSEMBL].includes(seq.resultData.dbHandle)
      ? DBEnsemblItemView
      : NonEnsemblItemView;
    return (
      <ItemViewComponent
        sequence={seq}
        updateImportOptions={(newOptions: Partial<DBImportItemOptions>) => {
          updateImportOptions(newOptions, idx);
        }}
        sequenceImportOptions={sequenceImportOptions}
      />
    );
  }

  function getResultTitle(seq: DBImportSequence): string {
    if (seq.resultData.name?.length > 0) {
      return seq.resultData.name;
    } else if (seq.resultData.acc.length > 0) {
      return seq.resultData.acc;
    } else {
      // Private Ensembl results might not have an accession name in its data but all other DB sources do
      const selectedVersion = seq.resultData.versions![seq.importOptions.selectedVersionIndex!];
      return selectedVersion.gene.ensemblId;
    }
  }

  if (errorMessage != null) {
    return (
      <div className={css(sharedItemStyles.resultsContainer)}>
        <InlineBanner bannerType={InlineBannerType.WARNING} text={errorMessage} />
      </div>
    );
  }
  if (sequences.length === 0) {
    return <></>;
  }
  if (sequences.length === 1) {
    return (
      <div
        className={css(sharedItemStyles.resultsContainer)}
        {...getTestProps({component: 'DBImportSequence', element: 'item', key: 'result'})}
      >
        {renderResultContent(sequences[0], 0)}
      </div>
    );
  }

  return (
    <div
      className={css(sharedItemStyles.resultsContainer)}
      {...getTestProps({component: 'DBImportSequence', element: 'item', key: 'result'})}
    >
      <p className={css(styles.multiResultText)}>Please select the sequence(s) you would like to import.</p>
      {sequences.map((seq, idx) => (
        <div key={idx}>
          <CollapsibleResultContainer
            idx={idx}
            isSelected={seq.importOptions.isSelected}
            isExpanded={expandedSections.includes(idx)}
            isSelectionDisabled={
              seq.resultData.dbHandle === DBHandle.ADDGENE && seq.resultData.sequences?.length === 0
            }
            onChangeSelected={(checked: boolean) => {
              updateImportOptions({isSelected: checked}, idx);
            }}
            onClick={() => {
              if (expandedSections.includes(idx)) {
                setExpandedSections(expandedSections.filter((section) => section !== idx));
              } else {
                setExpandedSections([...expandedSections, idx]);
              }
            }}
            title={getResultTitle(seq)}
          >
            {renderResultContent(seq, idx)}
          </CollapsibleResultContainer>
        </div>
      ))}
    </div>
  );
}

export const sharedItemStyles = StyleSheet.create({
  resultsContainer: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    paddingTop: '16px',
  },
  itemContainer: {
    marginBottom: '16px',
  },
  itemFlexContainer: {
    display: 'flex',
  },
  dbImageColumn: {
    width: '106px',
    marginRight: '16px',
  },
  dbImage: {
    width: '100%',
  },
  fullWidthColumn: {
    width: '100%',
  },
  itemInfoText: {
    ...FONT_PROXIMA_13,
    lineHeight: '16px',
    backgroundColor: GRAY_LIGHT,
    borderRadius: '3px',
    padding: '6px 12px',
    marginBottom: '16px',
    wordBreak: 'break-word',
  },
});

const styles = StyleSheet.create({
  collapsibleHeader: {
    display: 'flex',
    cursor: 'pointer',
    padding: '16px 0',
    marginTop: '16px',
  },
  headerWithBorder: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    marginTop: '0',
  },
  headerTitle: {
    margin: '0',
    width: '100%',
  },
  multiResultText: {
    marginBottom: '0',
  },
  itemColumn: {
    display: 'flex',
    marginRight: 16,
    minWidth: '50%',
    width: '50%',
  },
  rightInputsColumn: {
    width: '50%',
  },
});

export default DBImportItems;
